import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  I18nManager,
} from 'react-native';
import * as ExpoClipboard from 'expo-clipboard'; // BUG-005: replaced deprecated Clipboard from react-native
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import twoFactorService from '../services/twoFactorService';
import QRCode from 'react-native-qrcode-svg';
import logger from '../utils/logger';

export default function TwoFactorSetupScreen({ navigation }: any) {
  const { t } = useTranslation();
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const isRTL = I18nManager.isRTL;

  const [loading, setLoading] = useState(true);
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [step, setStep] = useState<'setup' | 'verify' | 'complete'>('setup');

  // Generate otpauth URL for QR code
  const getOtpauthUrl = () => {
    if (!secret || !user?.email) return '';
    const appName = 'IoT Edge Platform';
    return `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(
      user.email,
    )}?secret=${secret}&issuer=${encodeURIComponent(appName)}`;
  };

  useEffect(() => {
    loadTotpSetup();
  }, []);

  const loadTotpSetup = async () => {
    try {
      setLoading(true);
      logger.log('[2FA Setup] Loading TOTP setup...');
      const response = await twoFactorService.startTotpSetup();
      logger.log('[2FA Setup] Response received:', {
        hasSecret: !!response.secret,
        backupCodesCount: response.backupCodes?.length || 0,
      });
      setSecret(response.secret);
      setBackupCodes(response.backupCodes);
      logger.log('[2FA Setup] State updated successfully');
    } catch (error: any) {
      logger.error('[2FA Setup] Error:', error);
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        error.message ||
          (isRTL ? 'فشل تحميل إعدادات المصادقة الثنائية' : 'Failed to load 2FA setup'),
      );
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = async () => {
    await ExpoClipboard.setStringAsync(secret);
    Alert.alert(
      isRTL ? 'تم النسخ' : 'Copied',
      isRTL ? 'تم نسخ المفتاح السري' : 'Secret key copied to clipboard',
    );
  };

  const handleCopyBackupCode = async (code: string) => {
    await ExpoClipboard.setStringAsync(code);
    Alert.alert(
      isRTL ? 'تم النسخ' : 'Copied',
      isRTL ? 'تم نسخ رمز الاحتياطي' : 'Backup code copied to clipboard',
    );
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        isRTL ? 'الرجاء إدخال رمز مكون من 6 أرقام' : 'Please enter a 6-digit code',
      );
      return;
    }

    try {
      setVerifying(true);
      await twoFactorService.verifyTotpSetup(verificationCode);

      // Success - show alert and go back
      Alert.alert(
        isRTL ? 'نجح!' : 'Success!',
        isRTL ? 'تم تفعيل المصادقة الثنائية بنجاح' : '2FA enabled successfully',
        [
          {
            text: isRTL ? 'حسناً' : 'OK',
            onPress: () => navigation.goBack(),
          },
        ],
      );
    } catch (error: any) {
      const errorMessage = isRTL
        ? 'رمز التحقق غير صحيح. تأكد من:\n\n1. مسح رمز QR بشكل صحيح\n2. استخدام أحدث رمز من التطبيق\n3. ضبط وقت جهازك بشكل صحيح'
        : 'Invalid verification code. Make sure:\n\n1. QR code scanned correctly\n2. Using the latest code from app\n3. Device time is synced correctly';

      Alert.alert(isRTL ? 'فشل التحقق' : 'Verification Failed', error.message || errorMessage);
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons
            name={isRTL ? 'chevron-forward' : 'chevron-back'}
            size={28}
            color={theme.colors.text}
          />
        </TouchableOpacity>
        <Text style={[styles.title, { color: theme.colors.text }]}>
          {isRTL ? 'إعداد المصادقة الثنائية' : 'Setup Two-Factor Authentication'}
        </Text>
      </View>

      {step === 'setup' && (
        <>
          {/* Step 1: Scan QR Code */}
          <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
            <View style={styles.stepHeader}>
              <View style={[styles.stepNumber, { backgroundColor: theme.colors.primary }]}>
                <Text style={styles.stepNumberText}>1</Text>
              </View>
              <Text style={[styles.stepTitle, { color: theme.colors.text }]}>
                {isRTL ? 'امسح رمز QR' : 'Scan QR Code'}
              </Text>
            </View>

            <Text style={[styles.instruction, { color: theme.colors.textSecondary }]}>
              {isRTL
                ? 'افتح تطبيق Google Authenticator أو أي تطبيق مصادقة آخر وامسح الرمز أدناه:'
                : 'Open Google Authenticator or any authenticator app and scan the code below:'}
            </Text>

            <View style={styles.qrContainer}>
              {secret ? (
                <View style={styles.qrCodeWrapper}>
                  <QRCode
                    value={getOtpauthUrl()}
                    size={200}
                    color="#000000"
                    backgroundColor="#FFFFFF"
                  />
                </View>
              ) : (
                <View style={styles.qrPlaceholder}>
                  <ActivityIndicator size="large" color={theme.colors.primary} />
                  <Text style={[styles.qrPlaceholderText, { color: theme.colors.textSecondary }]}>
                    {isRTL ? 'جاري تحميل رمز QR...' : 'Loading QR code...'}
                  </Text>
                </View>
              )}
            </View>

            <View
              style={[
                styles.infoBox,
                { backgroundColor: 'rgba(33, 150, 243, 0.1)', marginTop: 16 },
              ]}
            >
              <Ionicons name="information-circle" size={20} color="#2196F3" />
              <Text
                style={[
                  styles.infoText,
                  { color: '#2196F3', flex: 1, marginLeft: 8, marginRight: 8 },
                ]}
              >
                {isRTL
                  ? 'تأكد من ضبط وقت جهازك تلقائياً للحصول على رموز صحيحة'
                  : 'Make sure your device time is set automatically for correct codes'}
              </Text>
            </View>

            <Text style={[styles.orText, { color: theme.colors.textSecondary }]}>
              {isRTL ? 'أو أدخل المفتاح يدوياً:' : 'Or enter the key manually:'}
            </Text>

            <View style={[styles.secretBox, { backgroundColor: theme.colors.background }]}>
              <Text style={[styles.secretText, { color: theme.colors.text }]} selectable>
                {secret || (isRTL ? 'جاري التحميل...' : 'Loading...')}
              </Text>
              <TouchableOpacity
                onPress={handleCopySecret}
                style={styles.copyButton}
                disabled={!secret}
              >
                <Ionicons
                  name="copy-outline"
                  size={20}
                  color={secret ? theme.colors.primary : theme.colors.textSecondary}
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* Step 2: Enter Code */}
          <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
            <View style={styles.stepHeader}>
              <View style={[styles.stepNumber, { backgroundColor: theme.colors.primary }]}>
                <Text style={styles.stepNumberText}>2</Text>
              </View>
              <Text style={[styles.stepTitle, { color: theme.colors.text }]}>
                {isRTL ? 'أدخل رمز التحقق' : 'Enter Verification Code'}
              </Text>
            </View>

            <Text style={[styles.instruction, { color: theme.colors.textSecondary }]}>
              {isRTL
                ? 'أدخل الرمز المكون من 6 أرقام من تطبيق المصادقة:'
                : 'Enter the 6-digit code from your authenticator app:'}
            </Text>

            <TextInput
              style={[
                styles.codeInput,
                {
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  borderColor: theme.colors.border,
                },
              ]}
              value={verificationCode}
              onChangeText={setVerificationCode}
              placeholder="123456"
              placeholderTextColor={theme.colors.textSecondary}
              keyboardType="number-pad"
              maxLength={6}
              textAlign="center"
            />

            <TouchableOpacity
              style={[
                styles.verifyButton,
                { backgroundColor: theme.colors.primary },
                verifying && styles.verifyButtonDisabled,
              ]}
              onPress={handleVerify}
              disabled={verifying || verificationCode.length !== 6}
            >
              {verifying ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <>
                  <Ionicons name="checkmark-circle" size={20} color="#fff" />
                  <Text style={styles.verifyButtonText}>{isRTL ? 'تفعيل' : 'Activate'}</Text>
                </>
              )}
            </TouchableOpacity>
          </View>

          {/* Step 3: Backup Codes */}
          <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
            <View style={styles.stepHeader}>
              <View style={[styles.stepNumber, { backgroundColor: theme.colors.primary }]}>
                <Text style={styles.stepNumberText}>3</Text>
              </View>
              <Text style={[styles.stepTitle, { color: theme.colors.text }]}>
                {isRTL ? 'احفظ رموز الاحتياطي' : 'Save Backup Codes'}
              </Text>
            </View>

            <Text style={[styles.instruction, { color: theme.colors.textSecondary }]}>
              {isRTL
                ? 'احفظ هذه الرموز في مكان آمن. يمكنك استخدامها لتسجيل الدخول إذا فقدت وصولك لتطبيق المصادقة.'
                : 'Save these codes in a safe place. You can use them to login if you lose access to your authenticator app.'}
            </Text>

            <View style={styles.backupCodesContainer}>
              {backupCodes.map((code, index) => (
                <TouchableOpacity
                  key={index}
                  style={[styles.backupCodeItem, { backgroundColor: theme.colors.background }]}
                  onPress={() => handleCopyBackupCode(code)}
                >
                  <Text style={[styles.backupCodeText, { color: theme.colors.text }]}>{code}</Text>
                  <Ionicons name="copy-outline" size={16} color={theme.colors.textSecondary} />
                </TouchableOpacity>
              ))}
            </View>

            <View style={[styles.warningBox, { backgroundColor: 'rgba(255, 152, 0, 0.1)' }]}>
              <Ionicons name="warning" size={20} color="#FF9800" />
              <Text style={[styles.warningText, { color: '#FF9800' }]}>
                {isRTL ? 'كل رمز يمكن استخدامه مرة واحدة فقط' : 'Each code can only be used once'}
              </Text>
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    paddingTop: 60,
  },
  backButton: {
    marginRight: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  card: {
    margin: 16,
    marginTop: 8,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumberText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  instruction: {
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 16,
  },
  qrContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  qrCodeWrapper: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  qrPlaceholder: {
    width: 232,
    height: 232,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
  },
  qrPlaceholderText: {
    marginTop: 12,
    fontSize: 14,
  },
  orText: {
    textAlign: 'center',
    fontSize: 14,
    marginVertical: 12,
  },
  secretBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  secretText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'monospace',
  },
  copyButton: {
    padding: 8,
  },
  codeInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 16,
    marginVertical: 12,
    alignItems: 'center',
  },
  codeText: {
    fontSize: 24,
    fontFamily: 'monospace',
    letterSpacing: 8,
  },
  verifyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
    gap: 8,
  },
  verifyButtonDisabled: {
    opacity: 0.6,
  },
  verifyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  backupCodesContainer: {
    marginTop: 12,
  },
  backupCodeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 6,
    marginBottom: 8,
  },
  backupCodeText: {
    fontFamily: 'monospace',
    fontSize: 14,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginTop: 16,
    gap: 8,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
  },
  infoText: {
    fontSize: 13,
    lineHeight: 18,
  },
  warningText: {
    flex: 1,
    fontSize: 13,
  },
});
